<template>
  <div>
    <AsidePanel
      ref="asidePanel"
      :components="components"
      :eventBus="eventBus"
      :handleSave="handleSave"
      :isExpanded="isExpanded"
      :itemId="itemId"
      :itemType="itemType"
      :redirectTo="redirectTo"
      :viewport="viewport"
    >
      <template v-slot:avatar>
        <AsideIcon
          icon="policyGroups"
        />
      </template>
      <template
        v-if="showSaveAsTemplateButton"
        v-slot:additionalActions
      >
        <ButtonSecondary
          :class="$style.templateButton"
          data-test-id="ConfigurationGroupAsidePanel__saveAsTemplate"
          :outline="false"
          text="save as template"
          textCase="lowercase"
          @click="handleSaveAsTemplate"
        />
      </template>
    </AsidePanel>
  </div>
</template>

<script>
import {
  ButtonSecondary,
} from '@jumpcloud/ui-components';
import {
  ItemActionConsts,
  ItemTableConsts,
  ItemTypeConsts,
  ItemTypeRoutes,
  StoreModelMap,
  StoreModuleMap,
} from 'client/util/Constants';
import {
  eventLocations,
  eventProperties,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { isArray } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import AsideIcon from 'client/components/AsidePanel/AsideIcon.vue';
import AsidePanel from 'client/components/AsidePanel/AsidePanel.vue';
import AsidePanelPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelPropsMixin';
import ConfigurationGroupDetails from 'client/groups/Configuration/ConfigurationGroupDetails.vue';
import ConfigurationGroupTemplateModal from 'client/groups/Configuration/Modals/ConfigurationGroupTemplateModal.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import ItemType from 'client/util/Constants/ItemType';
import ModalEventBus from 'components/Modal/ModalEventBus';
import MspState from 'client/components/models/MspState';
import PoliciesItemTable from 'client/components/ItemTable/PoliciesItemTable.vue';
import SaveReturnValue from 'client/components/AsidePanel/SaveReturnValue';
import SystemGroupItemTable from 'client/components/ItemTable/SystemGroupItemTable.vue';
import Vue from 'vue';

const { ConfigurationItemType, ConfigurationGroupItemType } = ItemType;
const configurationItem = new ConfigurationItemType();
const configurationGroupItem = new ConfigurationGroupItemType();

const { create, update } = ItemActionConsts;
const configurationGroupModel = StoreModelMap[ItemTypeConsts.configurationGroup];
const groupCollection = StoreModuleMap[ItemTypeConsts.configurationGroup];
const policyCollection = StoreModuleMap[ItemTypeConsts.configuration];
const storeModule = StoreModuleMap[ItemTypeConsts.policy];

const {
  description,
  location,
  section,
  text,
} = eventProperties;

const {
  policyGroupsPage,
} = eventLocations;

export default {
  name: 'ConfigurationGroupAsidePanel',

  components: {
    AsidePanel,
    AsideIcon,
    ButtonSecondary,
  },

  EventHelper,

  mixins: [
    AsidePanelPropsMixin,
  ],

  props: {
    itemType: {
      type: String,
      default: ItemTypeConsts.configurationGroup,
    },
  },

  data() {
    return {
      components: [
        {
          label: 'Details',
          vueComponent: ConfigurationGroupDetails,
        },
        {
          itemType: ItemTypeConsts.policy,
          label: configurationItem.labelPlural,
          headerLabels: {
            [create]: `The following ${configurationItem.labelPlural} will be applied to this group of policies`,
            [update]: `has the following ${configurationItem.labelPlural} applied`,
          },
          vueComponent: PoliciesItemTable,
          vueComponentProps: {
            boundItemsUrl: this.itemId
              ? `/api/v2/policygroups/${this.itemId}/membership`
              : undefined,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.groupAsidePanel,
          },
        },
        {
          itemType: ItemTypeConsts.systemGroup,
          label: 'Device Groups',
          headerLabels: {
            [create]: `This ${configurationGroupItem.label} is bound to the following device groups`,
            [update]: `${configurationGroupItem.label} is bound to the following device groups`,
          },
          vueComponent: SystemGroupItemTable,
          vueComponentProps: {
            boundItemsUrl: this.itemId
              ? `/api/v2/policygroups/${this.itemId}/systemgroups`
              : undefined,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.groupAsidePanel,
          },
        },
      ],
      itemHasBeenSaved: false,
      shouldCreateTemplate: false,
      validationErrorMessage: 'Please review the Details Tab for missing or invalid information',
      redirectTo: ItemTypeRoutes[ItemTypeConsts.configurationGroup],
      eventBus: new Vue(),
      storeModule,
      policyIds: [],
    };
  },

  computed: {

    ...mapGetters(policyCollection, [
      'getBoundItemsTotalCount',
      'getTotalCountOfAllItems',
      'getSelectedItemsCount',
      'getSelectedItems',
    ]),

    showSaveAsTemplateButton() {
      return !!this.$hasFeatureFlag(this.$featureFlags.policyGroupTemplateCreate)
      && MspState.isAnMsp
      && (this.isAdminWithBilling || this.isAdmin || this.isManager);
    },

    ...mapGetters('SettingsModule', [
      'isAdminWithBilling',
      'isAdmin',
      'isManager',
    ]),
  },

  methods: {
    ...mapActions(StoreModelMap[ItemTypeConsts.configurationGroup], [
      'fetchBoundPolicies',
    ]),

    ...mapActions(configurationGroupModel, [
      'saveItem',
      'createPolicyGroupTemplate',
      'bindConfigurationGroupToSystemGroups',
      'bindConfigurationGroupToPolicies',
    ]),

    ...mapActions(groupCollection, [
      'refreshCollection',
    ]),

    ...mapActions(notificationModule, [
      'triggerSuccessNotification',
      'triggerErrorNotification',
    ]),

    ...mapGetters(configurationGroupModel, [
      'hasItemChanged',
      'haveAssociationsChanged',
      'isItemBeingCreated',
    ]),

    async handleSave() {
      await this.$validator.validate();

      if (this.$validator.errors.any()) {
        throw Error(this.validationErrorMessage);
      }

      const item = await (this.shouldCreateTemplate
        ? this.validateTemplateCreation()
        : this.saveItem()
      );

      if (!item) {
        throw Error('Unexpected error');
      }

      this.shouldCreateTemplate = false;
      this.itemHasBeenSaved = true;

      await this.bindConfigurationGroupToPolicies();
      await this.bindConfigurationGroupToSystemGroups();
      await this.refreshCollection({ item });

      return new SaveReturnValue();
    },

    async handleSaveAsTemplate() {
      await this.$validator.validate();

      if (this.$validator.errors.any()) {
        throw Error(this.validationErrorMessage);
      }

      const hasPolicies = await this.itHasBoundItemsForTemplate();
      if (!hasPolicies) {
        ModalEventBus.open({
          isLargeModal: false,
          component: ConfigurationGroupTemplateModal,
        });
        return;
      }
      this.shouldCreateTemplate = true;
      this.$options.EventHelper.triggerPolicySaveAsTemplateClicked({
        [description]: 'Save Policy Group Template Button Clicked',
        [location]: policyGroupsPage,
        [section]: 'Footer',
        [text]: 'Save Policy Group and Policy Group Template',
      });
      this.eventBus.$emit('mainActionSaveButtonClick');
    },

    hasModelChanged() {
      if (this.itemHasBeenSaved) {
        return false;
      }

      return this.hasItemChanged() || this.haveAssociationsChanged();
    },

    async itHasBoundItemsForTemplate() {
      // if policies tab never selected
      if (!this.getTotalCountOfAllItems && this.itemId) {
        try {
          // validates if existing policies are bound to show or hide modal
          const result = await this.fetchBoundPolicies({ id: this.itemId });
          if (isArray(result) && result.length) {
            // gets all policies to bind to template
            const fullFetch = await this.fetchBoundPolicies({ id: this.itemId, limit: 1000 });
            this.policyIds = fullFetch.map(r => r.id);
            return true;
          }
          return false;
        } catch (e) {
          return false;
        }
      }
      // gets all policies to bind to template if policies tab is selected
      // and at least 1 policy bound
      if (this.getSelectedItemsCount) {
        this.policyIds = this.getSelectedItems;
        return true;
      }
      // no policies bound so show modal
      return false;
    },

    async validateTemplateCreation() {
      // We need to make sure the Policy Group saves successfully first before
      // we create the Template.
      const item = await this.saveItem();

      try {
        await this.createPolicyGroupTemplate(this.policyIds);
        this.triggerSuccessNotification({
          message: 'Policy Group Template saved successfully!',
          title: '',
        });
      } catch {
        // At this point, the Policy Group saved successfully, but our Policy
        // Group Template did not. We don't want to keep them in the aside panel
        // but do want them to know something went wrong.
        this.triggerErrorNotification({
          message: 'Failed to Save the Template.',
          title: 'Failed to Save',
          showCloseButton: true,
        });
      }

      return item;
    },
  },
};
</script>

<style module scoped>
.templateButton {
  line-height: 0.8;
  margin-right: var(--jc-spacer-medium);
}
</style>
